/* eslint-disable react/prop-types */
import React from 'react';
import { Loader2, Users, Sparkles } from 'lucide-react';

/**
 * TableFloorView — owner ask Feb 2026: the serving panel's floor view
 * should look like the till's floor view, and both should use the same
 * colour code (red = occupied, green = available, blue = clearing).
 * Plus: make the till tile a little smaller and visually creative.
 *
 * Shared between POSPage (`/pos`) and WaiterPanel (`/serving`). Each
 * caller passes its own action handler that fires when staff tap a
 * tile — Till opens the order/settle view, Waiter advances the table
 * status.
 *
 * The "compact" prop drops the legend, shrinks the tiles, and removes
 * the inner padding — handy for embedding inside another panel that
 * already has its own header.
 */

const sizeCls = {
  // Default: dense grid, smaller tiles than the original 190×140 brick.
  // The new size feels like a hospitality floorplan tile rather than a
  // stat card, which is what the owner asked for.
  default: {
    grid: 'grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2.5 p-4',
    tile: 'min-h-[112px] p-3',
    table: 'text-base',
    badge: 'text-[9px]',
  },
  compact: {
    grid: 'grid-cols-[repeat(auto-fill,minmax(130px,1fr))] gap-2 px-1 py-2',
    tile: 'min-h-[96px] p-2.5',
    table: 'text-[15px]',
    badge: 'text-[9px]',
  },
};

function StatusDot({ kind }) {
  // Tiny pulsing dot — gives the tile a "live" feel without animating
  // the whole card. Four states map to the owner-approved palette.
  const cls =
    kind === 'occupied'
      ? 'bg-rose-400 shadow-[0_0_8px_rgba(244,63,94,0.7)]'
      : kind === 'cleaning'
      ? 'bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.7)]'
      : kind === 'settled_seated'
      ? 'bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.7)]'
      : 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.55)]';
  return (
    <span
      className={`w-1.5 h-1.5 rounded-full ${cls} ${kind === 'occupied' ? 'animate-pulse' : ''}`}
    />
  );
}

function FloorTile({ table, onAvailable, onOccupied, onCleaning, size = 'default' }) {
  const sz = sizeCls[size];
  const tabs = table.active_tabs || table.tabs || [];
  // Cleaning state is server-controlled; occupancy is derived from live
  // tabs (table.status can lag if sibling orders close without flipping
  // it). Same logic the till had inline — kept identical so the two
  // surfaces never disagree.
  const isCleaning = table.status === 'cleaning';
  const occupied = !isCleaning && tabs.length > 0;
  // Owner ask Feb 2026: bill paid but guests still seated. Backend
  // stamps `settled_seated_since` on the table and skips the auto-flip
  // to cleaning. Treat as its own state — amber, dedicated tile —
  // so floor staff can see at a glance which tables are "physically
  // taken but commercially closed" and tap to clear when guests leave.
  const isSettledSeated = !isCleaning && !occupied && !!table.settled_seated_since;
  const total = tabs.reduce(
    (s, t) => s + Math.max(0, Number(t.current_total || 0) - Number(t.paid_amount || 0)),
    0
  );
  const names = tabs.map((t) => t.customer_name).filter(Boolean);

  // Shared visual: top corner carries a circular table-number medallion
  // (creative touch — looks like a real venue floorplan token) and the
  // bottom carries the contextual primary action. No more 3-stacked
  // buttons; one tap == one decision.
  const base = `relative group flex flex-col justify-between rounded-md border-2 ${sz.tile} text-left transition-all active:scale-[0.98] overflow-hidden`;

  if (isCleaning) {
    return (
      <button
        type="button"
        onClick={() => onCleaning && onCleaning(table)}
        className={`${base} border-sky-500/40 bg-sky-500/[0.06] hover:border-sky-300 hover:bg-sky-500/15`}
        data-testid={`floor-tile-${table.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 text-sky-300 uppercase font-sans tracking-[0.16em] text-[9px]">
            <StatusDot kind="cleaning" />
            Clearing
          </div>
          <span className={`font-serif font-semibold text-sky-100 ${sz.table} leading-none`}>
            T{table.number}
          </span>
        </div>
        <p
          className="text-[10px] text-sky-300/80 font-sans uppercase tracking-[0.12em] mt-auto"
          data-testid={`floor-tile-${table.id}-action`}
        >
          Tap → free up
        </p>
      </button>
    );
  }

  if (isSettledSeated) {
    // Bill paid · guests still seated. Tap to clear (same handler as
    // the cleaning tile — flips status → available + clears the
    // settled_seated_since stamp on the backend).
    return (
      <button
        type="button"
        onClick={() => onCleaning && onCleaning(table)}
        className={`${base} border-amber-500/45 bg-amber-500/[0.07] hover:border-amber-300 hover:bg-amber-500/15`}
        data-testid={`floor-tile-${table.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 text-amber-300 uppercase font-sans tracking-[0.16em] text-[9px]">
            <StatusDot kind="settled_seated" />
            Paid · Seated
          </div>
          <span className={`font-serif font-semibold text-amber-100 ${sz.table} leading-none`}>
            T{table.number}
          </span>
        </div>
        <p
          className="text-[10px] text-amber-300/80 font-sans uppercase tracking-[0.12em] mt-auto"
          data-testid={`floor-tile-${table.id}-action`}
        >
          Tap → clear
        </p>
      </button>
    );
  }

  if (!occupied) {
    return (
      <button
        type="button"
        onClick={() => onAvailable && onAvailable(table)}
        className={`${base} border-emerald-500/35 bg-emerald-500/[0.05] hover:border-emerald-300 hover:bg-emerald-500/15`}
        data-testid={`floor-tile-${table.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 text-emerald-300 uppercase font-sans tracking-[0.16em] text-[9px]">
            <StatusDot kind="available" />
            Free
          </div>
          <span className={`font-serif font-semibold text-white ${sz.table} leading-none`}>
            T{table.number}
          </span>
        </div>
        <div className="flex items-end justify-between mt-auto">
          {table.capacity ? (
            <span className="text-[10px] text-zinc-500 font-sans inline-flex items-center gap-1">
              <Users className="w-3 h-3" />
              {table.capacity}
            </span>
          ) : (
            <span />
          )}
          <span className="text-emerald-300 text-[10px] font-sans uppercase tracking-[0.12em] opacity-0 group-hover:opacity-100 transition">
            Open →
          </span>
        </div>
      </button>
    );
  }

  // Occupied — animated red, names truncated, outstanding $ pinned right.
  return (
    <button
      type="button"
      onClick={() => onOccupied && onOccupied(table)}
      className={`${base} border-rose-500/45 bg-rose-500/[0.07] hover:border-rose-300 hover:bg-rose-500/15`}
      data-testid={`floor-tile-${table.id}`}
    >
      {/* Subtle corner glow — passive "this table is hot" cue */}
      <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition" style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(244,63,94,0.12), transparent 60%)' }} />
      <div className="flex items-start justify-between gap-2 relative">
        <div className="flex items-center gap-1.5 text-rose-300 uppercase font-sans tracking-[0.16em] text-[9px]">
          <StatusDot kind="occupied" />
          {tabs.length} {tabs.length === 1 ? 'tab' : 'tabs'}
        </div>
        <span className={`font-serif font-semibold text-white ${sz.table} leading-none`}>
          T{table.number}
        </span>
      </div>
      {names.length > 0 && (
        <p
          className="text-[11px] text-zinc-200 font-sans leading-snug line-clamp-2 mt-1.5 relative"
          data-testid={`floor-tile-${table.id}-names`}
        >
          {names.join(' · ')}
        </p>
      )}
      <div className="flex items-end justify-between mt-auto relative">
        <span className="text-[10px] text-zinc-500 font-sans uppercase tracking-[0.12em]">
          Tap → settle
        </span>
        <span
          className="font-mono text-gold text-sm font-semibold"
          data-testid={`floor-tile-${table.id}-total`}
        >
          ${total.toFixed(2)}
        </span>
      </div>
    </button>
  );
}

export default function TableFloorView({
  tables,
  loading,
  onAvailable,
  onOccupied,
  onCleaning,
  size = 'default',
  showLegend = true,
  testId = 'floor-view',
}) {
  const sz = sizeCls[size];
  // Same legend math the till used — derived only from live tabs +
  // server-set cleaning state. Sums to tables.length by construction.
  const cleaningCount = tables.filter((t) => t.status === 'cleaning').length;
  const occupiedCount = tables.filter(
    (t) => t.status !== 'cleaning' && (t.active_tabs || t.tabs || []).length > 0
  ).length;
  const settledSeatedCount = tables.filter(
    (t) =>
      t.status !== 'cleaning' &&
      (t.active_tabs || t.tabs || []).length === 0 &&
      !!t.settled_seated_since
  ).length;
  const availableCount = tables.length - occupiedCount - cleaningCount - settledSeatedCount;

  return (
    <div className="flex-1 overflow-y-auto" data-testid={testId}>
      {showLegend && (
        <div className="px-6 py-3 border-b border-white/5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-5 text-xs font-sans uppercase tracking-[0.16em]">
            <span className="text-emerald-300 flex items-center gap-1.5">
              <StatusDot kind="available" /> {availableCount} Free
            </span>
            <span className="text-rose-300 flex items-center gap-1.5">
              <StatusDot kind="occupied" /> {occupiedCount} Occupied
            </span>
            {settledSeatedCount > 0 && (
              <span className="text-amber-300 flex items-center gap-1.5">
                <StatusDot kind="settled_seated" /> {settledSeatedCount} Paid · Seated
              </span>
            )}
            {cleaningCount > 0 && (
              <span className="text-sky-300 flex items-center gap-1.5">
                <StatusDot kind="cleaning" /> {cleaningCount} Clearing
              </span>
            )}
          </div>
          <span className="hidden md:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-zinc-600 font-sans">
            <Sparkles className="w-3 h-3" /> Live · 8s
          </span>
        </div>
      )}
      {loading && tables.length === 0 ? (
        <div className="grid place-items-center h-60 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : tables.length === 0 ? (
        <p className="text-center text-zinc-500 text-sm py-20">No tables configured.</p>
      ) : (
        <div className={`grid ${sz.grid}`} data-testid={`${testId}-grid`}>
          {tables.map((t) => (
            <FloorTile
              key={t.id}
              table={t}
              onAvailable={onAvailable}
              onOccupied={onOccupied}
              onCleaning={onCleaning}
              size={size}
            />
          ))}
        </div>
      )}
    </div>
  );
}
